import { useCallback, useEffect, useState } from 'react';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import type { VariantHistoryNervResponse, VariantHistoryNervRow } from '@/shared/types/variantHistoryNerv';

// Daily NERV Trends read path (FIG-238 · UC-257): the stored ∑: NERV per PT day from Variant History,
// through variant-history-nerv-v1. There is deliberately NO fallback: when the snapshots cannot be
// loaded the hook reports an error, and the chart shows it instead of recalculating anything.

export type VariantHistoryNervStatus = 'loading' | 'ready' | 'error';

export interface VariantHistoryNervState {
  status: VariantHistoryNervStatus;
  /** Only the snapshots of the requested range, and only when they loaded. */
  snapshots: VariantHistoryNervRow[];
  error: string | null;
  refetch: () => void;
}

interface Loaded {
  key: string;
  status: 'ready' | 'error';
  snapshots: VariantHistoryNervRow[];
}

export const SNAPSHOT_LOAD_ERROR = 'Could not load the Variant History snapshots.';

const isResponse = (value: unknown): value is VariantHistoryNervResponse => {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return v.success === true && Array.isArray(v.snapshots);
};

/**
 * Loads the stored snapshots for `organisationId` between `startDate` and `endDate` (PT calendar
 * dates, 'YYYY-MM-DD'). While the requested range differs from the loaded one the state is
 * 'loading' with no snapshots, so a previous range's points are never drawn against new dates.
 */
export function useVariantHistoryNerv(
  organisationId: string | null | undefined,
  startDate: string,
  endDate: string,
): VariantHistoryNervState {
  const key = `${organisationId ?? ''}|${startDate}|${endDate}`;
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!organisationId) return;
    let cancelled = false;
    const requestKey = key;

    (async () => {
      try {
        const { data, error } = await invokeFunction<unknown>(supabase, 'variant-history-nerv-v1', {
          body: { organisationId, startDate, endDate },
        });
        if (cancelled) return;
        if (error || !isResponse(data)) {
          if (error) console.error('Error loading Variant History snapshots:', error);
          setLoaded({ key: requestKey, status: 'error', snapshots: [] });
          return;
        }
        setLoaded({ key: requestKey, status: 'ready', snapshots: data.snapshots });
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading Variant History snapshots:', err);
        setLoaded({ key: requestKey, status: 'error', snapshots: [] });
      }
    })();

    return () => {
      cancelled = true;
    };
    // `key` covers organisationId, startDate and endDate; `attempt` re-runs the same request.
  }, [key, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = useCallback(() => {
    setLoaded(null);
    setAttempt((n) => n + 1);
  }, []);

  if (!organisationId) return { status: 'error', snapshots: [], error: SNAPSHOT_LOAD_ERROR, refetch };
  const current = loaded && loaded.key === key ? loaded : null;
  return {
    status: current?.status ?? 'loading',
    snapshots: current?.status === 'ready' ? current.snapshots : [],
    error: current?.status === 'error' ? SNAPSHOT_LOAD_ERROR : null,
    refetch,
  };
}
